import { createBrowserRouter } from 'react-router-dom';
import { ErrorPage } from '@/pages/shared/error';
import { GuestOnly, RequireAdmin, RequireAuth, RequireStoreRole, RootRedirect, StoreScope } from './guards';
import { InitialLoading } from './InitialLoading';
import { AuthLayout, ClientLayout, OwnerLayout } from './layouts';

export const router = createBrowserRouter([
  {
    path: '/',
    HydrateFallback: InitialLoading,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <RootRedirect /> },

      // /login, /register — redirect away if already signed in.
      {
        element: <GuestOnly />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: 'login',
                lazy: async () => {
                  const { LoginPage } = await import('@/pages/auth/login');
                  return { Component: LoginPage };
                },
              },
              {
                path: 'register',
                lazy: async () => {
                  const { RegisterPage } = await import('@/pages/auth/register');
                  return { Component: RegisterPage };
                },
              },
            ],
          },
        ],
      },

      // Every authenticated zone.
      {
        element: <RequireAuth />,
        children: [
          {
            path: 'stores',
            lazy: async () => {
              const { StoresPage } = await import('@/pages/stores');
              return { Component: StoresPage };
            },
          },
          {
            path: 'stores/new',
            lazy: async () => {
              const { CreateStorePage } = await import('@/pages/stores');
              return { Component: CreateStorePage };
            },
          },
          {
            path: 'stores/join',
            lazy: async () => {
              const { JoinStorePage } = await import('@/pages/stores');
              return { Component: JoinStorePage };
            },
          },

          // §3.8: администратор системы — глобальный флаг на пользователе, не завязан на магазин.
          {
            element: <RequireAdmin />,
            children: [
              {
                path: 'admin/owners',
                lazy: async () => {
                  const { AdminOwnersPage } = await import('@/pages/admin/owners');
                  return { Component: AdminOwnersPage };
                },
              },
            ],
          },

          // §1: владельческий раздел — /s/:storeId/...
          {
            path: 's/:storeId',
            element: <StoreScope />,
            children: [
              {
                element: <RequireStoreRole role="OWNER" />,
                children: [
                  {
                    element: <OwnerLayout />,
                    children: [
                      { index: true, lazy: async () => ({ Component: (await import('@/pages/owner/documents')).PurchasesPage }) },
                      {
                        path: 'categories',
                        lazy: async () => {
                          const { CategoriesPage } = await import('@/pages/owner/categories');
                          return { Component: CategoriesPage };
                        },
                      },
                      {
                        path: 'counterparties',
                        lazy: async () => {
                          const { CounterpartiesPage } = await import('@/pages/owner/counterparties');
                          return { Component: CounterpartiesPage };
                        },
                      },
                      {
                        path: 'counterparties/:id',
                        lazy: async () => {
                          const { CounterpartyDetailPage } = await import('@/pages/owner/counterparties');
                          return { Component: CounterpartyDetailPage };
                        },
                      },
                      {
                        path: 'purchases',
                        lazy: async () => {
                          const { PurchasesPage } = await import('@/pages/owner/documents');
                          return { Component: PurchasesPage };
                        },
                      },
                      {
                        path: 'sales',
                        lazy: async () => {
                          const { SalesPage } = await import('@/pages/owner/documents');
                          return { Component: SalesPage };
                        },
                      },
                      {
                        path: 'returns-in',
                        lazy: async () => {
                          const { ReturnsInPage } = await import('@/pages/owner/documents');
                          return { Component: ReturnsInPage };
                        },
                      },
                      {
                        path: 'returns-out',
                        lazy: async () => {
                          const { ReturnsOutPage } = await import('@/pages/owner/documents');
                          return { Component: ReturnsOutPage };
                        },
                      },
                      {
                        path: 'write-offs',
                        lazy: async () => {
                          const { WriteOffsPage } = await import('@/pages/owner/documents');
                          return { Component: WriteOffsPage };
                        },
                      },
                      {
                        path: 'inventories',
                        lazy: async () => {
                          const { InventoriesPage } = await import('@/pages/owner/documents');
                          return { Component: InventoriesPage };
                        },
                      },
                      {
                        path: 'cash-orders',
                        lazy: async () => {
                          const { CashOrdersPage } = await import('@/pages/owner/cash-orders');
                          return { Component: CashOrdersPage };
                        },
                      },
                      {
                        path: 'order-requests',
                        lazy: async () => {
                          const { OrderRequestsPage } = await import('@/pages/owner/order-requests');
                          return { Component: OrderRequestsPage };
                        },
                      },
                      {
                        path: 'join-requests',
                        lazy: async () => {
                          const { JoinRequestsPage } = await import('@/pages/owner/join-requests');
                          return { Component: JoinRequestsPage };
                        },
                      },
                      {
                        path: 'audit-log',
                        lazy: async () => {
                          const { AuditLogPage } = await import('@/pages/owner/audit-log');
                          return { Component: AuditLogPage };
                        },
                      },
                      {
                        path: 'settings',
                        lazy: async () => {
                          const { SettingsPage } = await import('@/pages/owner/settings');
                          return { Component: SettingsPage };
                        },
                      },
                      {
                        path: 'reports/stock',
                        lazy: async () => {
                          const { StockReportPage } = await import('@/pages/owner/reports');
                          return { Component: StockReportPage };
                        },
                      },
                      {
                        path: 'reports/turnover',
                        lazy: async () => {
                          const { TurnoverReportPage } = await import('@/pages/owner/reports');
                          return { Component: TurnoverReportPage };
                        },
                      },
                      {
                        path: 'reports/sales',
                        lazy: async () => {
                          const { SalesReportPage } = await import('@/pages/owner/reports');
                          return { Component: SalesReportPage };
                        },
                      },
                      {
                        path: 'reports/profit',
                        lazy: async () => {
                          const { ProfitReportPage } = await import('@/pages/owner/reports');
                          return { Component: ProfitReportPage };
                        },
                      },
                      {
                        path: 'reports/sales-profit',
                        lazy: async () => {
                          const { SalesProfitReportPage } = await import('@/pages/owner/reports');
                          return { Component: SalesProfitReportPage };
                        },
                      },
                      {
                        path: 'reports/write-offs',
                        lazy: async () => {
                          const { WriteOffsReportPage } = await import('@/pages/owner/reports');
                          return { Component: WriteOffsReportPage };
                        },
                      },
                      {
                        path: 'reports/debts',
                        lazy: async () => {
                          const { DebtsReportPage } = await import('@/pages/owner/reports');
                          return { Component: DebtsReportPage };
                        },
                      },
                      {
                        path: 'reports/expiry',
                        lazy: async () => {
                          const { ExpiryReportPage } = await import('@/pages/owner/reports');
                          return { Component: ExpiryReportPage };
                        },
                      },
                      {
                        path: 'reports/negative-stock',
                        lazy: async () => {
                          const { NegativeStockReportPage } = await import('@/pages/owner/reports');
                          return { Component: NegativeStockReportPage };
                        },
                      },
                      {
                        path: 'reports/provisional-sales',
                        lazy: async () => {
                          const { ProvisionalSalesReportPage } = await import('@/pages/owner/reports');
                          return { Component: ProvisionalSalesReportPage };
                        },
                      },
                    ],
                  },
                  // §13: печатная форма — без сайдбара/шапки, отдельная страница для печати браузером.
                  {
                    path: 'print/documents/:id',
                    lazy: async () => {
                      const { DocumentPrintPage } = await import('@/pages/owner/print');
                      return { Component: DocumentPrintPage };
                    },
                  },
                ],
              },
            ],
          },

          // §9: кабинет клиента — /c/:storeId/...
          {
            path: 'c/:storeId',
            element: <StoreScope />,
            children: [
              {
                element: <RequireStoreRole role="CLIENT" />,
                children: [
                  {
                    element: <ClientLayout />,
                    children: [
                      { index: true, lazy: async () => ({ Component: (await import('@/pages/client/documents')).ClientDocumentsPage }) },
                      {
                        path: 'documents',
                        lazy: async () => {
                          const { ClientDocumentsPage } = await import('@/pages/client/documents');
                          return { Component: ClientDocumentsPage };
                        },
                      },
                      {
                        path: 'documents/:id',
                        lazy: async () => {
                          const { ClientDocumentDetailPage } = await import('@/pages/client/documents');
                          return { Component: ClientDocumentDetailPage };
                        },
                      },
                      {
                        path: 'payments',
                        lazy: async () => {
                          const { ClientPaymentsPage } = await import('@/pages/client/payments');
                          return { Component: ClientPaymentsPage };
                        },
                      },
                      {
                        path: 'reconciliation',
                        lazy: async () => {
                          const { ClientReconciliationPage } = await import('@/pages/client/reconciliation');
                          return { Component: ClientReconciliationPage };
                        },
                      },
                      {
                        path: 'catalog',
                        lazy: async () => {
                          const { ClientCatalogPage } = await import('@/pages/client/catalog');
                          return { Component: ClientCatalogPage };
                        },
                      },
                      {
                        path: 'cart',
                        lazy: async () => {
                          const { ClientCartPage } = await import('@/pages/client/cart');
                          return { Component: ClientCartPage };
                        },
                      },
                      {
                        path: 'order-requests',
                        lazy: async () => {
                          const { ClientOrderRequestsPage } = await import('@/pages/client/order-requests');
                          return { Component: ClientOrderRequestsPage };
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        path: '*',
        lazy: async () => {
          const { NotFoundPage } = await import('@/pages/shared/not-found');
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
